/**
 * @fileoverview NavigationButton component that renders a button with customizable label, type, and onClick handler.
 * It also handles processing state and displays a spinner when processing.
 *
 * @example
 * // Basic usage
 * <NavigationButton label="Click me" type="confirm" onClick={() => {}} />
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../config'
import css from './NavigationButton.module.css'

interface ButtonProps {
    label: string
    type: 'confirm' | 'cancel'
    isDisabled?: boolean
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<unknown>
}

export function NavigationButton(props: ButtonProps) {
    const [isProcessing, setIsProcessing] = useState<boolean>(false)
    const { t } = useTranslation()

    const isButtonDisabled = props.isDisabled || isProcessing

    async function onClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        setIsProcessing(true)
        await props.onClick(e)
        setTimeout(() => {
            setIsProcessing(false)
        }, 500)
    }

    return (
        <button
            disabled={isButtonDisabled}
            onClick={onClick}
            className={`${css.button} ${css[props.type]}`}
        >
            <span>{t(props.label)}</span>
            {isProcessing ? (
                <img
                    alt="progress"
                    className={css.spinner}
                    src={config.s3.getStaticURL('loader.svg')}
                />
            ) : null}
        </button>
    )
}
